<template>
    <div class="relative">
        <div class="menu-button">
            <button
                class="flex justify-center items-center w-10 h-10 rounded-full bg-surface-e duration-200 overflow-hidden ring-2 ring-white ring-opacity-0 hover:ring-opacity-40"
                @click="toggleIsMenuVisible"
                data-testid="account-menu-button"
            >
                <img
                    class="w-14 h-14 p-2"
                    alt="logo"
                    src="../../assets/svg/clere-target-light.svg"
                />
            </button>
            <span
                    v-if="notificationsCount > 0"
                    class="absolute top-0 right-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold"
            >
                    {{ notificationsCount }}
            </span>
    </div>

        <div
            class="menu flex flex-col items-center absolute top-12 right-0 bg-surface-d p-shadow-4 rounded text-sm z-50"
            :class="{
                'menu-closed': !isMenuVisible,
            }"
        >
            <div
                class="w-full px-6 pt-4 pb-2"
                v-if="isMultiUser"
            >
                Signed into Org:
                <span class="font-semibold">{{
                    `${organization.name}`
                }}</span>
            </div>
            <div
                class="w-full px-6 pt-4 pb-2"
                v-else-if="!isMasquereded"
            >
                Signed in as
                <span class="font-semibold">{{
                    `${account.firstName} ${account.lastName}`
                }}</span>
            </div>
            <div
                class="w-full px-6 pt-4 pb-2"
                v-else-if="isMasquereded"
            >
                Masqueraded as
                <span class="font-semibold">{{ `${masquerededName}` }}</span>
            </div>

            <div class="divider" />
            <div
                class="cursor-pointer block w-full px-6 py-2 bg-white bg-opacity-0 duration-200 hover:bg-opacity-20"
                to="/dashboard"
                @click="clickLink({route: '/dashboard'})"
            >
                <div style="display: flex; flex-direction: row;">
                    {{ "My Dashboard" }}
                    <span
                            v-if="notificationsCount > 0"
                            class="top-0 right-0 flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-xs font-bold"
                            style="margin-left: 10px; top: 0; font-size: 9px;"
                    >
                            {{ notificationsCount }}
                    </span>
                </div>
            </div>
            <div
                class="cursor-pointer block w-full px-6 py-2 bg-white bg-opacity-0 duration-200 hover:bg-opacity-20"
                v-for="(link, i) in links"
                :key="i"
                :to="link.route"
                @click="clickLink(link)"
            >
                {{ link.title }}
            </div>
            <div class="divider" />
            <div
                class="block w-full px-6 py-2 mb-2 cursor-pointer bg-white bg-opacity-0 duration-200 hover:bg-opacity-20"
                @click="logOut"
            >
                Sign out
            </div>
        </div>

        <div
            class="fixed top-0 left-0 w-screen h-screen z-40"
            v-if="isMenuVisible"
            @click="toggleIsMenuVisible"
        />
    </div>
</template>

<script lang="ts">
// vue
import { defineComponent, ref, computed, watch } from '@vue/composition-api';
import { useStore } from '@/store';
import { useRouter } from '@/router';

// services
import { useService } from '@/vue3/api/services';

// lib
import { Portal } from '@linusborg/vue-simple-portal';

export default defineComponent({
    components: {
        Portal,
    },
    setup() {
        // hooks
        const { state, getters } = useStore();
        const router = useRouter();
        const {
            logOut,
            getAccount,
            getAllPermissionNameByAccountId,
            getOrganization,
            getCourseSchemas,
            addState,
        } = useService();

        const isMultiUser = computed(()=>{
            const isMasq = JSON.parse(
                localStorage.getItem('tempAuthToken') as string);
            if(isMasq){
                return false;
            }
            const multiUserFlag1 = (state.accountPermissions != null ? (state.accountPermissions.indexOf('canEnableMultiOrgAccount') !== -1 || state.multiUserAuthToken) : false);
            const multiUserFlag2 = ref<any>(JSON.parse(localStorage.getItem('multiUserAuthToken') as string));
            if(multiUserFlag2 || multiUserFlag1){
                return true;
            }
            return false;
        })

        // computed
        const links = computed(() => {
            let linkArray: any[] = [
                { route: '/my-account', title: 'My Account' },
                { route: '/feedback', title: 'Feedback' },
                { route: '/privacy-policy', title: 'Privacy Policy' },
                { route: '/settings', title: 'Settings' },
            ];

            if(isMultiUser.value){
                linkArray.splice(1, 0, { route: '/switch-account', title: 'Switch Account' })
            }

            if (JSON.parse(state.tempAuthToken as string)) {
                linkArray.push({
                    id: 'revertToAdministrator',
                    route: '/admin/accounts',
                    title: 'Revert to Administrator',
                });
            }

            return linkArray;
        });

        const notificationsCount = ref(state.notificationCount || 0);

        // reactive
        const isMenuVisible = ref(false);

        // methods
        const toggleIsMenuVisible = () => {
            isMenuVisible.value = !isMenuVisible.value;
        };

        watch(
            () => state.notificationCount,
            () => {notificationsCount.value = state.notificationCount},
        );

        const clickLink = async (link: any) => {
            toggleIsMenuVisible();
            if (link.id === 'revertToAdministrator') {
                await revertToAdmin();
            }
            router.push({ path: link.route });
        };

        const revertToAdmin = async () => {
            state.tempAuthToken = null;
            state.masqueradeAccountPermissions = null;

            localStorage.removeItem('tempAuthToken');
            localStorage.removeItem('masqueradeAccountPermissions');

            const permsProm = getAllPermissionNameByAccountId(
                JSON.parse(state.authToken as string).accountId,
            );

            const orgProm = getOrganization(
                JSON.parse(state.authToken as string).organizationId,
            );

            const coursesProm = getCourseSchemas();

            const accountProm = getAccount(
                JSON.parse(state.authToken as string).accountId,
            );

            const perms = await permsProm;
            const org = await orgProm;
            const courses = await coursesProm;
            const account = await accountProm;

            addState('account', account);
            addState('accountPermissions', perms);
            addState('courseSchemas', courses);
            addState('currentOrganization', org);

            if (!localStorage.getItem('currentCourseSchema')) {
                if (JSON.parse(state.courseSchemas as string).length > 0) {
                    state.currentCourseSchema = JSON.stringify(
                        JSON.parse(state.courseSchemas as string)[0],
                    );
                    localStorage.setItem(
                        'currentCourseSchema',
                        JSON.stringify(
                            JSON.parse(state.courseSchemas as string)[0],
                        ),
                    );
                }
            }
            window.location.reload();
        };

        return {
            account: getters.account,
            isMasquereded: JSON.parse(
                localStorage.getItem('tempAuthToken') as string,
            ),
            organization: getters.currentOrganization,
            isMultiUser,
            masquerededName: localStorage.getItem('masquerededUserName'),
            logOut,
            isMenuVisible,
            toggleIsMenuVisible,
            links,
            clickLink,
            notificationsCount,
        };
    },
});
</script>

<style scoped>
.menu {
    width: 200px;
    transition-duration: 0.2s;
}

.menu-closed {
    pointer-events: none;
    opacity: 0;
    transform: translateY(-8px) translateZ(0);
}

.divider {
    width: 80%;
    padding-bottom: 5px;
    margin-bottom: 5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.menu-button {
    position: relative; /* Ensure child elements can be positioned absolutely */
}

.menu-button span {
    transform: translate(50%, -50%); /* Position the badge properly */
}
</style>
